import { useState } from 'react';

import type { AnalyticsEvents } from '@sunflower-games/analytics-events';
import { CLICK_IDS } from '@sunflower-games/analytics-events';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useEffectOnce } from 'react-use';

import coinsHeader from '../../../../../assets/coins-header.png';
import warningSign from '../../../../../assets/warning-sign.svg';
import Button from '../../../../../components/buttons/Button';
import { I18Text } from '../../../../../components/i18Next/I18Text';
import { CONTACT_SUPPORT_URL } from '../../../../../config/constants';
import { SkipToken } from '../../../../../hooks/useCertainCleanup';
import { analytics } from '../../../../../services/analytics';
import { intercom } from '../../../../../services/intercom';
import { useFlag } from '../../../../../services/launchDarkly';
import type { PocState } from '../../../../../stores/poc/types';
import type { RootStore } from '../../../../../stores/RootStore';
import { useStores } from '../../../../../stores/useStores';
import type { PocViewFactory } from '../../../types';
import { PocViewConstructor } from '../../shared/PocViewConstructor/PocViewConstructor';
import { usePocProcess } from '../../shared/usePocProcess';
import { withCurrentSessionState } from '../../shared/withCurrentSessionState';
import { FailedPOCView } from '../FailedPOCView/FailedPOCView';
import { RequestPOCView } from '../RequestPOCView/RequestPOCView';

import styles from './InitPocView.module.scss';

const cleanup = async (stores: RootStore, pocState: PocState<'init', 'redemption'>) => {
  void analytics.sendEvent('automatic_poc', {
    step: 'automatic_poc_request_displayed',
    event_action: 'click',
    triggered_by: 'cancel_redeem',
    attempt_number: pocState.commonData.attemptNumber + 1,
    amount: pocState.extraData.redemptionAmount,
    click_id: CLICK_IDS.POC.CANCEL_REDEEM,
  });

  stores.pocStore.hangCurrentPocSession();
  await stores.payRedeemStore.cancelRedeemTransaction(pocState.extraData.redemptionRequestId);
  await stores.payRedeemStore.fetchRedeemTransactions();
  stores.payRedeemStore.setInitialState();
};

const getImpressionAnalyticsEvent = (pocState: PocState<'init'>): AnalyticsEvents['automatic_poc'] => {
  switch (pocState.flow) {
    case 'redemption':
      return {
        step: 'automatic_poc_request_displayed',
        event_action: 'impression',
        triggered_by: 'redeem',
        amount: pocState.extraData.redemptionAmount,
        request_reason: pocState.commonData.requestReason,
        attempt_number: pocState.commonData.attemptNumber + 1,
      };
    case 'failed':
      if (pocState.extraData.failureType === 'retry') {
        return {
          step: 'reached_max_attempt',
          event_action: 'impression',
          triggered_by: 'credit_card_declined',
        };
      }

      return {
        step: 'automatic_poc_failure',
        event_action: 'impression',
        attempts: pocState.commonData.attemptNumber,
        triggered_by: pocState.extraData.failureType === 'failed' ? 'system_error' : 'credit_card_declined',
      };
    case 'hanging':
      return {
        step: 'automatic_poc_request_displayed',
        event_action: 'impression',
        triggered_by: 'manual',
        amount: null,
        attempt_number: 1,
        request_reason: 'manual',
      };
  }
};

const getClickAnalyticsEvent = (pocState: PocState<'init'>): AnalyticsEvents['automatic_poc'] => {
  switch (pocState.flow) {
    case 'hanging':
      return {
        step: 'automatic_poc_started',
        event_action: 'click',
        click_id: CLICK_IDS.POC.LETS_DO_IT,
        amount: null,
        attempt_number: pocState.commonData.attemptNumber + 1,
        triggered_by: 'lets_do_it_click',
      };
    case 'redemption':
      return {
        step: 'automatic_poc_started',
        event_action: 'click',
        click_id: CLICK_IDS.POC.LETS_DO_IT,
        amount: pocState.extraData.redemptionAmount,
        attempt_number: pocState.commonData.attemptNumber + 1,
        triggered_by: 'lets_do_it_click',
      };
    case 'failed':
      return {
        step: 'automatic_poc_started',
        event_action: 'click',
        click_id: CLICK_IDS.POC.TRY_AGAIN,
        amount: null,
        attempt_number: pocState.commonData.attemptNumber + 1,
        triggered_by: 'try_again',
      };
  }
};

export const initPocViewFactory: PocViewFactory = {
  factoryName: 'pocView',
  factory: (stepsMapping, fallbackStep, { onClose }) =>
    withCurrentSessionState(
      function InitPocView(props) {
        const stores = useStores();
        const redesignDialogs = useFlag('ui_redesign_dialogs', false);
        const [isProcessing, setIsProcessing] = useState(false);
        const { t } = useTranslation();

        usePocProcess(props.isCurrent);

        const isRedemptionFlow = props.pocState.flow === 'redemption';

        useEffectOnce(() => {
          void analytics.sendEvent('automatic_poc', getImpressionAnalyticsEvent(props.pocState));
        });

        const handleOkClick = async () => {
          void analytics.sendEvent('automatic_poc', getClickAnalyticsEvent(props.pocState));

          setIsProcessing(true);
          const initiatedSuccessfully = await stores.pocStore.initPocSession();
          setIsProcessing(false);

          if (initiatedSuccessfully) {
            props.nextStep();
          } else {
            props.forceStep(stepsMapping.pocStuck);
          }
        };

        const handleContactSupportClick = (canNotRetry?: boolean) => {
          void analytics.sendEvent('automatic_poc', {
            step: 'automatic_poc_contact_support',
            event_action: 'click',
            triggered_by: canNotRetry ? 'reached_max_attempt' : 'contact_support_button',
          });
          intercom.handleOpenSupport();
        };

        const handleContactSupportClickV2 = (canNotRetry?: boolean) => {
          void analytics.sendEvent('automatic_poc', {
            step: 'automatic_poc_contact_support',
            event_action: 'click',
            triggered_by: canNotRetry ? 'reached_max_attempt' : 'contact_support_button',
          });
          stores.portalStore.closePortal();
          intercom.handleOpenSupport();
        };

        if (props.pocState.flow === 'failed') {
          const canNotRetry = props.pocState.extraData.failureType !== 'retry';

          if (redesignDialogs) {
            return (
              <FailedPOCView
                onClose={onClose}
                onContactSupport={() => handleContactSupportClickV2(canNotRetry)}
                onTryAgain={handleOkClick}
              />
            );
          }

          return (
            <PocViewConstructor
              content={
                <>
                  <div className={styles.failurePrompt}>
                    <p className={styles.accent}>
                      {t('poc.cardVerificationFailed', {
                        cardNumberEnding: props.pocState.commonData.cardNumberEnding,
                      })}
                    </p>
                    <p>{t('poc.redemptionPendingNote')}</p>
                  </div>

                  {props.pocState.extraData.failureType === 'retry' && (
                    <Button btnType="main" className={styles.okButton} disabled={isProcessing} onClick={handleOkClick}>
                      {t('common.tryAgainUpperFIrstLetter')}
                    </Button>
                  )}

                  <a
                    className={classNames('button-action support-button animation-popup', styles.contactSupportButton)}
                    href={CONTACT_SUPPORT_URL}
                    rel="noreferrer"
                    target="_blank"
                    onClick={() => handleContactSupportClick(canNotRetry)}
                  >
                    {t('common.contactSupportFirstLetterUpper')}
                  </a>
                </>
              }
              newDesign={redesignDialogs}
              title={
                <>
                  <img alt="warning sign" className={styles.warningSign} src={warningSign} />
                  {t('poc.verificationIssueTitle')}
                </>
              }
              onClose={onClose}
            />
          );
        }

        const makeCancelHandler = (state: PocState<'init', 'redemption'>) => async () => {
          setIsProcessing(true);
          await cleanup(stores, state);
          setIsProcessing(false);
          props.forceStep(fallbackStep);
        };

        if (redesignDialogs && props.pocState.flow === 'hanging') {
          return (
            <RequestPOCView
              cardNumberEnding={props.pocState.commonData.cardNumberEnding}
              onClose={onClose}
              onContactSupport={handleContactSupportClickV2}
              onOk={handleOkClick}
            />
          );
        }

        return (
          <PocViewConstructor
            content={
              <>
                <I18Text
                  className={styles.firstTimeRequest}
                  components={{ regular: <span className={styles.accent} /> }}
                  i18nKey="poc.pleaseCompleteVerificationPrefix"
                  values={{
                    cardNumberEnding: props.pocState.commonData.cardNumberEnding,
                  }}
                />

                <Button btnType="main" className={styles.okButton} disabled={isProcessing} onClick={handleOkClick}>
                  {t('kyc.letsDoIt')}
                </Button>

                {props.pocState.flow === 'redemption' ? (
                  <Button btnType="main" disabled={isProcessing} onClick={makeCancelHandler(props.pocState)}>
                    {t('poc.cancelRedeem')}
                  </Button>
                ) : (
                  <a
                    className={classNames('button-action support-button animation-popup', styles.contactSupportButton)}
                    href={CONTACT_SUPPORT_URL}
                    rel="noreferrer"
                    target="_blank"
                    onClick={() => handleContactSupportClick()}
                  >
                    {t('common.contactSupportFirstLetterUpper')}
                  </a>
                )}
              </>
            }
            newDesign={redesignDialogs}
            title={
              isRedemptionFlow ? (
                t('poc.almostThere')
              ) : (
                <>
                  <img alt={t('poc.coinsHeader')} className={styles.coinsHeader} src={coinsHeader} />
                  {t('poc.cardVerification')}
                </>
              )
            }
            onClose={isRedemptionFlow ? undefined : onClose}
          />
        );
      },
      'init',
      {
        getArgs: (pocState) => (pocState.flow === 'redemption' && pocState.step === 'init' ? pocState : SkipToken),
        cleanupFn: (pocState, stores) => {
          void cleanup(stores, pocState);
        },
      },
    ),
};
